import { useMemo, useState } from 'react';
import { ScreenHeader } from '../../components/layout/ScreenHeader';
import { PageContainer } from '../../components/layout/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ApiErrorCard } from '../../components/ui/ApiErrorCard';
import { Checkbox } from '../../components/ui/Checkbox';
import { Modal } from '../../components/ui/Modal';
import { NumberStepper } from '../../components/ui/NumberStepper';
import { TaperChart } from '../../components/charts/TaperChart';
import { TaperMinimap } from '../../components/charts/TaperMinimap';
import { DoseTimingChart } from '../../components/charts/DoseTimingChart';
import { LastWeekDoseList } from '../../components/charts/LastWeekDoseList';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { TaperAdvisorCard } from './TaperAdvisorCard';
import { useAuth } from '../../auth/AuthContext';
import { findHabitualDoseTimes } from '@shared/doseTimingPattern';
import { groupDosesByDayAndMinute } from '../../lib/doseTimingGrouping';
import { useApi } from '../../hooks/useApi';
import { apiPost } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';
import { useToast } from '../../components/ui/Toast';
import { fmtMg, localYmd, ymdAddDays } from '../../lib/format';
import { useToday } from '../../lib/useToday';
import {
  pickDefaultTaperWindow,
  readStoredTaperOverlay,
  readStoredTaperWindow,
  readStoredTimingWindow,
  resolveTaperWindow,
  timingWindowDays,
  writeStoredTaperOverlay,
  writeStoredTaperWindow,
  writeStoredTimingWindow,
  type DoseTimingWindow,
  type TaperWindowMode,
} from '../../lib/taperWindow';
import { computeTaperSchedule } from '@shared/taperAlgorithm';
import type { Dose, TaperPlanWire } from '@shared/types';

export function TaperScreen() {
  const toast = useToast();
  const { effectiveTz } = useAuth();
  const planRes = useApi<TaperPlanWire | null>('/api/taper');
  const dosesRes = useApi<Dose[]>('/api/doses?limit=1000');
  const today = useToday();

  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [newWeeks, setNewWeeks] = useState(12);
  const [busy, setBusy] = useState(false);
  // Chart view window. `null` means "no explicit choice yet" → fall
  // back to the smart default keyed off how far along the user is
  // (see pickDefaultTaperWindow). Once they tap a segment, the choice
  // sticks via localStorage.
  const [explicitWindow, setExplicitWindow] = useState<TaperWindowMode | null>(
    () => readStoredTaperWindow(),
  );
  const [overlayEnabled, setOverlayEnabled] = useState<boolean>(() => readStoredTaperOverlay());
  const [timingWindow, setTimingWindow] = useState<DoseTimingWindow>(() => readStoredTimingWindow());
  // Part 12 §P12.4 A4 — required when recompute curve hits an
  // 'aggressive' or 'cold_turkey' band. Server enforces; UI surfaces.
  const [ackAggressive, setAckAggressive] = useState(false);

  // Live preview of the recompute curve. Mirrors onboarding so a user
  // recomputing to a too-fast schedule gets the same warning + ack
  // they'd see during initial setup.
  const recomputePreview = useMemo(() => {
    if (!recomputeOpen) return null;
    const baseline = planRes.status === 'ok' && planRes.data ? planRes.data.baselineMg : 40;
    if (baseline <= 0) return null;
    return computeTaperSchedule({ baselineMg: baseline, startDate: today, weeks: newWeeks });
  }, [recomputeOpen, planRes, newWeeks, today]);
  const recomputeNeedsAck = recomputePreview?.warnings.some(
    (w) => w.kind === 'aggressive' || w.kind === 'cold_turkey',
  ) ?? false;

  const actualByDate = useMemo(() => {
    if (dosesRes.status !== 'ok') return {} as Record<string, number>;
    const map: Record<string, number> = {};
    for (const d of dosesRes.data) {
      // Bucket by the user's *local* calendar day, not the UTC slice of
      // `ts`. A dose logged at 8 pm Tijuana (UTC-7) has ts=…T03:00Z next
      // day; the raw slice would attribute it to tomorrow, which both
      // undercounts "today's chart bar" and inflates tomorrow's.
      const key = localYmd(d.ts);
      map[key] = (map[key] ?? 0) + d.computedMg7oh;
    }
    Object.keys(map).forEach((k) => (map[k] = Math.round(map[k] * 10) / 10));
    return map;
  }, [dosesRes]);

  const todayIndex = useMemo(() => {
    if (planRes.status !== 'ok' || !planRes.data) return -1;
    return planRes.data.days.findIndex((d) => d.date === today);
  }, [planRes, today]);

  const todayTarget = planRes.status === 'ok' && planRes.data ? planRes.data.days.find((d) => d.date === today)?.targetMg ?? 0 : 0;
  const todayActual = actualByDate[today] ?? 0;

  const effectiveWindow: TaperWindowMode =
    explicitWindow ?? (todayIndex >= 0 ? pickDefaultTaperWindow(todayIndex) : 'full');
  const planDays = planRes.status === 'ok' && planRes.data ? planRes.data.days.length : 0;
  const { startIndex: windowStartIndex, endIndex: windowEndIndex } = resolveTaperWindow(
    effectiveWindow,
    Math.max(0, todayIndex),
    planDays,
  );
  const windowLen = windowEndIndex - windowStartIndex + 1;

  // Previous-period overlay points. Independent of `overlayEnabled` so
  // we can decide whether to even SHOW the toggle (no point in offering
  // a compare when there's nothing to compare against).
  const potentialOverlay = useMemo(() => {
    if (planRes.status !== 'ok' || !planRes.data) return [] as Array<{ i: number; mg: number }>;
    const out: Array<{ i: number; mg: number }> = [];
    for (const d of planRes.data.days) {
      if (d.dayIndex < windowStartIndex || d.dayIndex > windowEndIndex) continue;
      const prevDate = ymdAddDays(d.date, -windowLen);
      const mg = actualByDate[prevDate];
      if (typeof mg === 'number') out.push({ i: d.dayIndex, mg });
    }
    return out;
  }, [planRes, windowStartIndex, windowEndIndex, windowLen, actualByDate]);

  // Only meaningful in Week / Month — "Full" view's previous period
  // would be the prior taper plan (not yet wired) so we hide the toggle.
  const overlayApplicable = effectiveWindow !== 'full' && potentialOverlay.length > 0;
  const overlayPoints = overlayApplicable && overlayEnabled ? potentialOverlay : [];

  // Trailing 7-day average band — only shown in Month/Full views (where
  // same-day comparisons are too noisy). For each day in the window we
  // sum the prior 7 days' actuals (treating missing days as 0) and
  // divide by 7 to get a smoothed mg/day. Renders as a soft area band
  // behind the actual line in TaperChart.
  const rolling7AvgPoints = useMemo(() => {
    if (planRes.status !== 'ok' || !planRes.data) return [] as Array<{ i: number; mg: number }>;
    if (windowLen <= 14) return [];
    const out: Array<{ i: number; mg: number }> = [];
    for (const d of planRes.data.days) {
      if (d.dayIndex < windowStartIndex || d.dayIndex > windowEndIndex) continue;
      let sum = 0;
      for (let back = 0; back < 7; back += 1) {
        const prev = ymdAddDays(d.date, -back);
        const v = actualByDate[prev];
        if (typeof v === 'number') sum += v;
      }
      out.push({ i: d.dayIndex, mg: sum / 7 });
    }
    return out;
  }, [planRes, windowStartIndex, windowEndIndex, windowLen, actualByDate]);

  // Tomorrow's target — used in the caption. The chart itself draws an
  // outline circle at todayIndex+1 unconditionally.
  const tomorrowTarget = planRes.status === 'ok' && planRes.data
    ? planRes.data.days[todayIndex + 1]?.targetMg ?? 0
    : 0;
  const baseline = planRes.status === 'ok' && planRes.data ? planRes.data.baselineMg : 0;
  const pctBelowBaseline = baseline > 0
    ? Math.max(0, Math.round(((baseline - todayTarget) / baseline) * 100))
    : 0;

  function handleWindowChange(next: TaperWindowMode): void {
    setExplicitWindow(next);
    writeStoredTaperWindow(next);
  }

  function handleOverlayChange(next: boolean): void {
    setOverlayEnabled(next);
    writeStoredTaperOverlay(next);
  }

  function handleTimingWindowChange(next: DoseTimingWindow): void {
    setTimingWindow(next);
    writeStoredTimingWindow(next);
  }

  // Dose-timing rows for the new 24h chart. We always compute today +
  // (N-1) prior days; the chart fades older rows by recency. Habitual
  // peaks (≥2 occurrences over the last 14 days) are passed in as
  // optional vertical bands — only relevant once the user has enough
  // history to show a pattern.
  const timingRows = useMemo(() => {
    if (dosesRes.status !== 'ok') return [];
    return groupDosesByDayAndMinute(dosesRes.data, {
      tz: effectiveTz,
      daysBack: timingWindowDays(timingWindow),
    });
  }, [dosesRes, effectiveTz, timingWindow]);
  const habitualPeaks = useMemo(() => {
    if (dosesRes.status !== 'ok') return [];
    return findHabitualDoseTimes(dosesRes.data, { tz: effectiveTz, daysBack: 14 });
  }, [dosesRes, effectiveTz]);

  async function applyHold() {
    setBusy(true);
    try {
      await apiPost('/api/taper/hold', { fromDate: today, days: 2, reason: 'user_requested' });
      toast.push('2-day hold applied');
      planRes.refresh();
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not apply hold.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function recompute() {
    setBusy(true);
    try {
      const baseline = planRes.status === 'ok' && planRes.data ? planRes.data.baselineMg : 40;
      await apiPost('/api/taper/plan', {
        baselineMg: baseline,
        startDate: today,
        weeks: newWeeks,
        acknowledgedAggressive: ackAggressive || undefined,
      });
      toast.push('Plan updated');
      planRes.refresh();
      setRecomputeOpen(false);
      setAckAggressive(false);
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not update plan.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageContainer>
      <ScreenHeader
        title="Taper"
        subtitle={
          planRes.status === 'ok' && planRes.data
            ? `Day ${Math.max(0, todayIndex)} of ${planRes.data.days.length - 1}`
            : undefined
        }
      />

      {/* The plan drives the whole screen — a failed /api/taper request
          must surface an explicit retry rather than leaving the user
          staring at a bare header. /api/doses errors fall back to the
          same card since the timing chart can't render without it. */}
      {planRes.status === 'error' ? (
        <ApiErrorCard error={planRes.error} onRetry={planRes.refresh} />
      ) : dosesRes.status === 'error' ? (
        <ApiErrorCard error={dosesRes.error} onRetry={dosesRes.refresh} />
      ) : null}

      {planRes.status === 'ok' && !planRes.data ? (
        <Card>
          <p className="muted">No taper plan yet. Run onboarding to set one up.</p>
        </Card>
      ) : null}

      {planRes.status === 'ok' && planRes.data ? (
        <div className="taper-grid">
          <Card>
            <div style={{ marginBlockEnd: 'var(--space-3)' }}>
              <SegmentedControl<TaperWindowMode>
                ariaLabel="Chart range"
                value={effectiveWindow}
                onChange={handleWindowChange}
                options={[
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                  { value: 'full', label: 'Full' },
                ]}
              />
            </div>
            {overlayApplicable ? (
              <div style={{ marginBlockEnd: 'var(--space-3)' }}>
                <Checkbox
                  checked={overlayEnabled}
                  onChange={handleOverlayChange}
                  label="Compare to previous period"
                />
              </div>
            ) : null}
            <TaperChart
              days={planRes.data.days}
              todayIndex={Math.max(0, todayIndex)}
              actualByDate={actualByDate}
              windowStartIndex={windowStartIndex}
              windowEndIndex={windowEndIndex}
              overlayPoints={overlayPoints}
              rolling7AvgPoints={rolling7AvgPoints}
            />
            <div style={{ marginBlockStart: 'var(--space-2)' }}>
              <TaperMinimap
                days={planRes.data.days}
                todayIndex={Math.max(0, todayIndex)}
                windowStartIndex={windowStartIndex}
                windowEndIndex={windowEndIndex}
              />
            </div>
            <p className="text-caption muted" style={{ marginBlockStart: 'var(--space-3)' }}>
              Today: <span className="mono">{fmtMg(todayTarget)}</span> target ·{' '}
              <span className="mono">{fmtMg(todayActual)}</span> so far
              {tomorrowTarget > 0 ? (
                <> · Tomorrow: <span className="mono">{fmtMg(tomorrowTarget)}</span></>
              ) : null}
              {pctBelowBaseline > 0 ? (
                <> · <span className="mono">{pctBelowBaseline}%</span> below baseline</>
              ) : null}
            </p>
          </Card>

          <Card>
            <h3 className="text-h3" style={{ marginBlockEnd: 'var(--space-3)' }}>Controls</h3>
            <div className="stack" style={{ '--stack-gap': 'var(--space-3)' } as React.CSSProperties}>
              <Button variant="secondary" onClick={applyHold} loading={busy}>
                Hold at today's level for 2 days
              </Button>
              <Button variant="secondary" onClick={() => setRecomputeOpen(true)}>
                Recompute schedule
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {planRes.status === 'ok' && planRes.data ? (
        <Card>
          <h3 className="text-h3" style={{ marginBlockEnd: 'var(--space-3)' }}>Last 7 days</h3>
          <LastWeekDoseList
            today={today}
            actualByDate={actualByDate}
            planDays={planRes.data.days}
            days={7}
          />
        </Card>
      ) : null}

      {planRes.status === 'ok' && planRes.data && dosesRes.status === 'ok' && dosesRes.data.length > 0 ? (
        <Card>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            marginBlockEnd: 'var(--space-3)',
            flexWrap: 'wrap',
          }}>
            <h3 className="text-h3" style={{ margin: 0 }}>Dose timing</h3>
            <SegmentedControl<DoseTimingWindow>
              ariaLabel="Dose timing window"
              value={timingWindow}
              onChange={handleTimingWindowChange}
              options={[
                { value: '1d', label: '1d' },
                { value: '3d', label: '3d' },
                { value: '7d', label: '7d' },
                { value: '14d', label: '14d' },
              ]}
            />
          </div>
          <DoseTimingChart rows={timingRows} habitualPeaks={habitualPeaks} />
          {habitualPeaks.length > 0 ? (
            <p className="text-caption muted" style={{ marginBlockStart: 'var(--space-2)' }}>
              Vertical bands mark times you typically dose, based on the last 14 days.
            </p>
          ) : null}
        </Card>
      ) : null}

      {/* Phase 5 PR-B §F3 — informational AI taper advisor. The card
          self-hides when the user is not enrolled in the feature flag
          (it probes GET /api/ai/taper-advisor on mount). Sits below
          the dose-timing chart so a user always sees their data before
          seeing the AI's framing of it. */}
      <TaperAdvisorCard />

      <Modal open={recomputeOpen} onOpenChange={setRecomputeOpen} title="Recompute schedule">
        <p className="muted" style={{ marginBlockEnd: 'var(--space-4)' }}>
          Set a new total duration from today. Your previous plan will be archived.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBlockEnd: 'var(--space-4)' }}>
          <NumberStepper value={newWeeks} onChange={setNewWeeks} min={2} max={52} step={1} large suffix="weeks" />
        </div>
        {recomputePreview ? (
          <p className="text-caption muted" style={{ marginBlockEnd: 'var(--space-3)' }}>
            About <span className="mono">{(recomputePreview.reductionPerWeek * 100).toFixed(0)}%</span> reduction per week.
          </p>
        ) : null}
        {recomputeNeedsAck ? (
          <div style={{
            marginBlockEnd: 'var(--space-3)',
            background: 'var(--color-warning-soft)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-ink-primary)',
          }}>
            {recomputePreview?.warnings.find((w) => w.kind === 'cold_turkey')
              ? "That's a very aggressive curve — cold-turkey-grade."
              : "That's an aggressive curve."}
            <div style={{ marginBlockStart: 'var(--space-3)' }}>
              <Checkbox
                checked={ackAggressive}
                onChange={setAckAggressive}
                label="I understand this pace exceeds standard medical guidance"
              />
            </div>
          </div>
        ) : null}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="tertiary" onClick={() => setRecomputeOpen(false)}>Cancel</Button>
          <Button
            onClick={recompute}
            loading={busy}
            disabled={recomputeNeedsAck && !ackAggressive}
          >
            Apply
          </Button>
        </div>
      </Modal>
    </PageContainer>
  );
}
